import * as React from "react";
import { useState, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { RootState } from "../../reducers/index";
import { usStates } from "../../../shared/constants/states"; // Adjust path if you placed it elsewhere
import { useAppSelector, useAppDispatch } from "../../store/index"; // useAppDispatch might be needed later for submission
import {
    useGetClientAddressQuery,
    useUpdateClientAddressMutation,
} from "../../reducers/registerapi"; // adjust path as needed
import {
    setCheckoutState,
} from "../../reducers/checkout";
import { ClientAddressUpdateRequest } from "../../interfaces/client";
import CheckoutClientHead from "./checkoutclienthead";

interface FormValues {
    address1: string;
    address2: string | null; // react-hook-form will typically handle this as string, null is fine for type
    city: string;
    state: string;
    postalCode: string;
    phone: string; // react-hook-form will typically handle this as string
}

const CheckoutAddress = () => {
    const { token } = useAppSelector((state: RootState) => state.checkout);
    const dispatch = useAppDispatch(); // Uncomment if you need to dispatch actions on submit

    const {
        register,
        handleSubmit,
        setValue, // To programmatically set form values
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: { // Initialize with empty strings or null for better controlled inputs
            address1: "",
            address2: "", // Inputs typically work best with strings
            city: "",
            state: "",
            postalCode: "",
            phone: "", // Inputs typically work best with strings
        },
    });

    // Fetch client address data
    const {
        data: clientAddressData,
        isLoading: isLoadingAddress,
        isError: isErrorAddress,
        // error: addressError, // You can use this for more specific error messages
    } = useGetClientAddressQuery(
        { token: token as string }, // Type assertion, ensure token is string when query runs
        { skip: !token } // Skip query if token is not available
    );

    // Mutation hook for updating the address
    const [updateClientAddress, { isLoading: isUpdatingAddress, error: updateError }] = // <-- Instantiate mutation
        useUpdateClientAddressMutation();

    // Effect to pre-populate form when clientAddressData is fetched
    useEffect(() => {
        if (clientAddressData) {
            setValue("address1", clientAddressData.address1 || "");
            setValue("address2", clientAddressData.address2 || "");
            setValue("city", clientAddressData.city || "");
            setValue("state", clientAddressData.state || "");
            setValue("postalCode", clientAddressData.postalCode || "");
            setValue("phone", clientAddressData.phone || "");
        }
    }, [clientAddressData, setValue]);

    const [isSubmitting, setIsSubmitting] = useState(false); // Placeholder for form submission loading state
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
        setServerErrors([]); // Clear previous server errors

        if (!token) {
            setServerErrors(["Authentication token is missing. Please log in again."]);
            return;
        }

        // Construct the request payload
        // The token is part of ClientAddressUpdateRequest but handled by the mutation's queryFn
        const updateRequest: ClientAddressUpdateRequest = {
            token, // Token for the Authorization header
            address1: data.address1 || null,
            address2: data.address2 || null,
            city: data.city || null,
            state: data.state || null,
            postalCode: data.postalCode || null,
            phone: data.phone || null,
        };

        try {
            // Call the mutation
            await updateClientAddress(updateRequest).unwrap(); // .unwrap() will throw on error

            // On success, dispatch the action to advance checkout state
            dispatch(setCheckoutState("agree"));

            // Optionally, show a success message or navigate
            // For now, we'll just log it and the state will change
            console.log("Address updated successfully, advancing to 'agree' state.");

        } catch (err: any) {
            console.error("Failed to update address:", err);
            let errorsToShow: string[] = ["Failed to update address. Please try again."];
            if (err.data && err.data.errors && Array.isArray(err.data.errors)) {
                errorsToShow = err.data.errors.map((e: any) => typeof e === 'string' ? e : e.message || "An unknown error occurred");
            } else if (err.data && err.data.message) {
                errorsToShow = [err.data.message];
            } else if (err.message) {
                errorsToShow = [err.message];
            }
            setServerErrors(errorsToShow);
        }
    };
    if (isLoadingAddress) {
        return (
            <div className="container mt-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading address information...</span>
                </div>
                <p className="mt-2">Loading address information...</p>
            </div>
        );
    }

    // Show error if fetching failed and we attempted to fetch (token was present)
    if (isErrorAddress && token) {
        return (
            <div className="alert alert-danger mt-3" role="alert">
                Could not load your address information. Please check your connection or try again later.
            </div>
        );
    }

    return (<>
        <CheckoutClientHead />
        <h3 className="mb-3">Address</h3>
        <form onSubmit={handleSubmit(onSubmit)} >

            {serverErrors.length > 0 && (
                <div className="alert alert-danger">
                    <ul className="mb-0">
                        {serverErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Address Line 1 */}
            <div className="row mb-3">
                <label htmlFor="inputAddress1" className="col-sm-3 col-form-label">Address Line 1*</label>
                <div className="col-sm-9">
                    <input
                        type="text"
                        className={`form-control ${errors.address1 ? 'is-invalid' : ''}`}
                        id="inputAddress1"
                        autoComplete="address-line1"
                        {...register("address1", {
                            required: "Address line 1 is required.",
                            minLength: { value: 3, message: "Address must be at least 3 characters." }
                        })}
                    />
                    {errors.address1 && (
                        <div className="invalid-feedback">
                            {errors.address1.message}
                        </div>
                    )}
                </div>
            </div>

            {/* Address Line 2 */}
            <div className="row mb-3">
                <label htmlFor="inputAddress2" className="col-sm-3 col-form-label">Address Line 2</label>
                <div className="col-sm-9">
                    <input
                        type="text"
                        className="form-control" // No 'is-invalid' logic unless specific validation is added
                        id="inputAddress2"
                        autoComplete="address-line2"
                        {...register("address2")}
                    />
                    {/* No error display for address2 unless specific validation is added */}
                </div>
            </div>

            {/* City */}
            <div className="row mb-3">
                <label htmlFor="inputCity" className="col-sm-3 col-form-label">City*</label>
                <div className="col-sm-9">
                    <input
                        type="text"
                        className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                        id="inputCity"
                        autoComplete="address-level2" // Standard autocomplete for city
                        {...register("city", {
                            required: "City is required.",
                            minLength: { value: 2, message: "City must be at least 2 characters." }
                        })}
                    />
                    {errors.city && (
                        <div className="invalid-feedback">
                            {errors.city.message}
                        </div>
                    )}
                </div>
            </div>
            <div className="row mb-3">
                <label htmlFor="inputState" className="col-sm-3 col-form-label">State*</label>
                <div className="col-sm-9">
                    <select
                        id="inputState"
                        className={`form-select ${errors.state ? 'is-invalid' : ''}`}
                        autoComplete="address-level1"
                        {...register("state", {
                            required: "State is required.",
                        })}
                    >
                        <option value="">- Select -</option>
                        {usStates.map((state) => (
                            <option key={state.abbreviation} value={state.abbreviation}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                    {errors.state && (
                        <div className="invalid-feedback">
                            {errors.state.message}
                        </div>
                    )}
                </div>
            </div>
            {/* Postal Code */}
            <div className="row mb-3">
                <label htmlFor="inputPostalCode" className="col-sm-3 col-form-label">Postal Code*</label>
                <div className="col-sm-9">
                    <input
                        type="text"
                        className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                        id="inputPostalCode"
                        autoComplete="postal-code"
                        {...register("postalCode", {
                            required: "Postal code is required.",
                        })}
                    />
                    {errors.postalCode && (
                        <div className="invalid-feedback">
                            {errors.postalCode.message}
                        </div>
                    )}
                </div>
            </div>

            <div className="row mb-3">
                <label htmlFor="inputPhone" className="col-sm-3 col-form-label">Phone*</label>
                <div className="col-sm-9">
                    <input
                        type="tel"
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        id="inputPhone"
                        autoComplete="tel"
                        {...register("phone", {
                            required: "Phone is required.",
                            // Optional: basic phone number pattern (very lenient)
                            // Complex phone validation is hard; consider libraries or backend validation primarily.
                        })}
                    />
                    {errors.phone && (
                        <div className="invalid-feedback">
                            {errors.phone.message}
                        </div>
                    )}
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-sm-9 offset-sm-3">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            "Save Address & Continue"
                        )}
                    </button>
                </div>
            </div>
        </form>
    </>);
};

export default CheckoutAddress;